import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	TPhaseCountsOfGeneralSeaAreas,
	TPhasePercentages,
	TSignalCountsOnSpeedRanges,
	TKeyMetrics,
	TVesselSpeedDraughtValues,
	TPolygonCalls, TVoyages, TStoppageEvents,
	actionTypes,
	TVesselVoyagesHourlyTracks,
} from './kpiTypes';
import { kpisActions } from './kpiSlice';
import { authApiV2 } from '../../axiosWrapper';
import { 
    trackEvent,
    trackExceptionWithContext, 
    LogLevel 
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';

function* getPhasePercentagesSaga({ payload: { imoNumber, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
	const requestId = `kpi-phase-percentages-${Date.now()}`;
	
	try {
		trackEvent(AppInsightsActions.KPI_PHASE_PERCENTAGES_LOAD, {
			requestId,
			imoNumber: imoNumber.toString(),
			dateRange: `${startYearMonth}-${endYearMonth}`,
			component: AppInsightsComponents.KPI_COMPONENT
		});

		const endpoint = `/phase-percentages`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startYearMonth, endYearMonth } });
		const newPhasePercentages: TPhasePercentages = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null,
			isFetched: true
		};

		trackEvent(AppInsightsActions.KPI_DATA_FETCH_SUCCESS, {
			requestId,
			action: 'phase-percentages',
			resultCount: response.data?.length?.toString() || '0',
			component: AppInsightsComponents.KPI_COMPONENT
		});

		yield put(kpisActions.getPhasePercentagesSuccessAction(newPhasePercentages));
	} catch (error: any) {
		trackExceptionWithContext(error, {
			component: AppInsightsComponents.KPI_COMPONENT,
			action: AppInsightsActions.KPI_DATA_FETCH_FAILURE,
			severity: LogLevel.Error,
			additionalData: {
				requestId,
				action: 'phase-percentages',
				imoNumber: imoNumber.toString(),
				errorType: AppInsightsErrorTypes.KPI_DATA_ERROR
			}
		});

		const newPhasePercentages: TPhasePercentages = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			isFetched: true,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getPhasePercentagesErrorAction(newPhasePercentages));
	}
}

function* getPhaseCountsOfGeneralSeaAreasSaga({ payload: { imoNumber, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/phase-counts-of-general-sea-areas`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startYearMonth, endYearMonth } });
		const newPhaseCountsOfGeneralSeaAreas: TPhaseCountsOfGeneralSeaAreas = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getPhaseCountsOfGeneralSeaAreasSuccessAction(newPhaseCountsOfGeneralSeaAreas));
	} catch (error: any) {
		const newPhaseCountsOfGeneralSeaAreas: TPhaseCountsOfGeneralSeaAreas = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getPhaseCountsOfGeneralSeaAreasErrorAction(newPhaseCountsOfGeneralSeaAreas));
	}
}

function* getSignalCountsOnSpeedRangesSaga({ payload: { imoNumber, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/signal-counts-on-speed-ranges`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startYearMonth, endYearMonth } });
		const newSignalCountsOnSpeedRanges: TSignalCountsOnSpeedRanges = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getSignalCountsOnSpeedRangesSuccessAction(newSignalCountsOnSpeedRanges));
	} catch (error: any) {
		const newSignalCountsOnSpeedRanges: TSignalCountsOnSpeedRanges = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getSignalCountsOnSpeedRangesErrorAction(newSignalCountsOnSpeedRanges));
	}
}

function* getVesselKeyMetricsSaga({ payload: { imoNumber, startYearMonth, endYearMonth } }: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/vessel-key-metrics`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startYearMonth, endYearMonth } });
		const newKeyMetrics: TKeyMetrics = {
			imoNumber: imoNumber!,
			data: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getVesselKeyMetricsSuccessAction(newKeyMetrics));

	} catch (error: any) {
		const newKeyMetrics: TKeyMetrics = {
			imoNumber: imoNumber!,
			data: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselKeyMetricsErrorAction(newKeyMetrics));
	}
}

function* getVesselSpeedAndDraughtOnLoadedConditionsSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/vessel-speed-and-draught-on-loaded-conditions`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startDate, endDate } });
		const newValues: TVesselSpeedDraughtValues = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getVesselSpeedAndDraughtOnLoadedConditionsSuccessAction(newValues));

	} catch (error: any) {
		const newValues: TVesselSpeedDraughtValues = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselSpeedAndDraughtOnLoadedConditionsErrorAction(newValues));
	}
}

function* getSummaryKeyMetricsSaga({ payload: { imoList, startYearMonth, endYearMonth } }: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/summary-key-metrics`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList, startYearMonth, endYearMonth });

		yield put(kpisActions.getSummaryKeyMetricsSuccessAction(response.data));

	} catch (error: any) {
		yield put(kpisActions.getSummaryKeyMetricsErrorAction(error));
	}
}

function* getSummaryPhaseCountsOfGeneralSeaAreasSaga({ payload: { imoList, startYearMonth, endYearMonth } }: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/summary-phase-counts-of-general-sea-areas`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList, startYearMonth, endYearMonth });

		yield put(kpisActions.getSummaryPhaseCountsOfGeneralSeaAreasSuccessAction(response.data));

	} catch (error: any) {
		yield put(kpisActions.getSummaryPhaseCountsOfGeneralSeaAreasErrorAction(error));
	}
}

function* getSummaryPhasePercentagesSaga({ payload: { imoList, startYearMonth, endYearMonth } }: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/summary-phase-percentages`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList, startYearMonth, endYearMonth });

		yield put(kpisActions.getSummaryPhasePercentagesSuccessAction(response.data));

	} catch (error: any) {
		yield put(kpisActions.getSummaryPhasePercentagesErrorAction(error));
	}
}

function* getSummarySignalCountsOnSpeedRangesSaga({ payload: { imoList, startYearMonth, endYearMonth } }: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/summary-signal-counts-on-speed-ranges`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList, startYearMonth, endYearMonth });

		yield put(kpisActions.getSummarySignalCountsOnSpeedRangesSuccessAction(response.data));

	} catch (error: any) {
		yield put(kpisActions.getSummarySignalCountsOnSpeedRangesErrorAction(error));
	}
}

function* getVesselPolygonCallsSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/vessel-polygon-calls`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startDate, endDate } });
		const newPolygonCalls: TPolygonCalls = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getVesselPolygonCallsSuccessAction(newPolygonCalls));

	} catch (error: any) {
		const newPolygonCalls: TPolygonCalls = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselPolygonCallsErrorAction(newPolygonCalls));
	}
}

function* getVesselVoyagesSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/vessel-voyages`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startDate, endDate } });
		const newVoyages: TVoyages = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null,
			isFetched: true
		};

		yield put(kpisActions.getVesselVoyagesSuccessAction(newVoyages));

	} catch (error: any) {
		const newVoyages: TVoyages = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			isFetched: true,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselVoyagesErrorAction(newVoyages));
	}
}

function* getVesselStoppageEventsSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/vessel-stoppage-events`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startDate, endDate } });
		const newStoppageEvents: TStoppageEvents = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getVesselStoppageEventsSuccessAction(newStoppageEvents));

	} catch (error: any) {
		const newStoppageEvents: TStoppageEvents = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselStoppageEventsErrorAction(newStoppageEvents));
	}
}

function* getVesselCohortVoyagesSaga(action: PayloadAction<{ imoList: number[], imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/vessel-cohort-voyages`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList: action.payload.imoList, startDate: action.payload.startDate, endDate: action.payload.endDate });

		const newVoyages: TVoyages = {
			imoNumber: action.payload.imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null,
			isFetched: true
		};

		yield put(kpisActions.getVesselCohortVoyagesSuccessAction(newVoyages));

	} catch (error: any) {
		const newVoyages: TVoyages = {
			imoNumber: action.payload.imoNumber!,
			dataList: null,
			loading: false,
			isFetched: true,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselCohortVoyagesErrorAction(newVoyages));
	}
}

function* getVesselCohortSignalCountsOnSpeedRangesSaga(action: PayloadAction<{ imoList: number[], imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/vessel-cohort-signal-counts-on-speed-ranges`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList: action.payload.imoList, startYearMonth: action.payload.startYearMonth, endYearMonth: action.payload.endYearMonth });
		const signalCount: TSignalCountsOnSpeedRanges = {
			imoNumber: action.payload.imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getVesselCohortSignalCountsOnSpeedRangesSuccessAction(signalCount));

	} catch (error: any) {
		const signalCount: TSignalCountsOnSpeedRanges = {
			imoNumber: action.payload.imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselCohortSignalCountsOnSpeedRangesErrorAction(signalCount));
	}
}


function* getVesselCohortPhasePercentagesOfPolygonCallsSaga(action: PayloadAction<{ imoList: number[], imoNumber: number, startYearMonth: number, endYearMonth: number }>) {
	try {
		const endpoint = `/vessel-cohort-phase-percentages-polygon-calls`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList: action.payload.imoList, startYearMonth: action.payload.startYearMonth, endYearMonth: action.payload.endYearMonth });
		const phasePercentages: TPhasePercentages = {
			imoNumber: action.payload.imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null,
			isFetched: true
		};

		yield put(kpisActions.getVesselCohortPhasePercentagesOfPolygonCallsSuccessAction(phasePercentages));

	} catch (error: any) {
		const phasePercentages: TPhasePercentages = {
			imoNumber: action.payload.imoNumber!,
			dataList: null,
			loading: false,
			isFetched: true,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselCohortPhasePercentagesOfPolygonCallsErrorAction(phasePercentages));
	}
}

function* getVesselVoyagesHourlyTracksSaga({ payload: { imoNumber, startDate, endDate } }: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/vessel-voyages-hourly-tracks`;

		const response: AxiosResponse<any> = yield call(authApiV2.get, endpoint, { params: { imoNumber, startDate, endDate } });
		const newPhasePercentages: TVesselVoyagesHourlyTracks = {
			imoNumber: imoNumber!,
			dataList: response.data,
			loading: false,
			loadingError: null
		};

		yield put(kpisActions.getVesselVoyagesHourlyTracksSuccessAction(newPhasePercentages));
	} catch (error: any) {
		const newPhasePercentages: TVesselVoyagesHourlyTracks = {
			imoNumber: imoNumber!,
			dataList: null,
			loading: false,
			loadingError: {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			},
		};

		yield put(kpisActions.getVesselVoyagesHourlyTracksErrorAction(newPhasePercentages));
	}
}

function* getActivityCoverageSaga({ payload: { imoList, startYearMonth, endYearMonth, startDate, endDate } }: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/activity-coverages`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList, startYearMonth, endYearMonth, startDate, endDate });

		yield put(kpisActions.getActivityCoverageSuccessAction(response.data));

	} catch (error: any) {
		yield put(kpisActions.getActivityCoverageErrorAction(error));
	}
}

function* getSectorActivityCoverageSaga({ payload: { imoList, startYearMonth, endYearMonth, startDate, endDate } }: PayloadAction<{ imoList: number[], startYearMonth: number, endYearMonth: number, startDate: string, endDate: string }>) {
	try {
		const endpoint = `/activity-coverages`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList, startYearMonth, endYearMonth, startDate, endDate });

		yield put(kpisActions.getSectorActivityCoverageSuccessAction(response.data));

	} catch (error: any) {
		yield put(kpisActions.getSectorActivityCoverageErrorAction(error));
	}
}

export function* watchKpiActions(): Generator {
	yield takeLatest(actionTypes.GET_SUMMARY_KEY_METRICS, getSummaryKeyMetricsSaga);
	yield takeLatest(actionTypes.GET_SUMMARY_PHASE_COUNTS_OF_GENERAL_SEA_AREAS, getSummaryPhaseCountsOfGeneralSeaAreasSaga);
	yield takeLatest(actionTypes.GET_SUMMARY_PHASE_PERCENTAGES, getSummaryPhasePercentagesSaga);
	yield takeLatest(actionTypes.GET_SUMMARY_SIGNAL_COUNTS_ON_SPEED_RANGES, getSummarySignalCountsOnSpeedRangesSaga);

	yield takeLatest(actionTypes.GET_PHASE_PERCENTAGES, getPhasePercentagesSaga);
	yield takeLatest(actionTypes.GET_PHASE_COUNTS_OF_GENERAL_SEA_AREAS, getPhaseCountsOfGeneralSeaAreasSaga);
	yield takeLatest(actionTypes.GET_SIGNAL_COUNTS_ON_SPEED_RANGES, getSignalCountsOnSpeedRangesSaga);
	yield takeLatest(actionTypes.GET_VESSEL_KEY_METRICS, getVesselKeyMetricsSaga);
	yield takeLatest(actionTypes.GET_VESSEL_SPEED_DRAUGHT_ON_LOADED_CONDITION, getVesselSpeedAndDraughtOnLoadedConditionsSaga);
	yield takeLatest(actionTypes.GET_VESSEL_POLYGON_CALLS, getVesselPolygonCallsSaga);
	yield takeLatest(actionTypes.GET_VESSEL_VOYAGES, getVesselVoyagesSaga);
	yield takeLatest(actionTypes.GET_VESSEL_STOPPAGE_EVENTS, getVesselStoppageEventsSaga);

	yield takeLatest(actionTypes.GET_VESSEL_COHORT_VOYAGES, getVesselCohortVoyagesSaga);
	yield takeLatest(actionTypes.GET_VESSEL_COHORT_SIGNAL_COUNTS_ON_SPEED_RANGES, getVesselCohortSignalCountsOnSpeedRangesSaga);
	yield takeLatest(actionTypes.GET_VESSEL_COHORT_PHASE_PERCENTAGES_OF_POLYGON_CALLS, getVesselCohortPhasePercentagesOfPolygonCallsSaga);
	yield takeLatest(actionTypes.GET_VESSEL_VOYAGES_HOURLY_TRACKS, getVesselVoyagesHourlyTracksSaga);
	yield takeLatest(actionTypes.GET_ACTIVITY_COVERAGE, getActivityCoverageSaga);
	yield takeLatest(actionTypes.GET_SECTOR_ACTIVITY_COVERAGE, getSectorActivityCoverageSaga);
}